/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';

import Numeric from '../numeric/numeric';
import ScheduleInfo from './schedule-info';

/**
 * Strategie přehrávání pro pozadí šablony.
 * @implements {ScheduleStrategy}
 */
class ScheduleBackground {
  /**
   * Vytvoří instanci.
   * @param {Session} session Sezení s nastavením zařízení.
   * @param {Layout} layout Šablona, pro kterou se plánuje pozadí.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   * @returns {ScheduleBackground} Nová instance.
   */
  constructor(session, layout, viewerRegistry) {
    if (!isObject(session)) {
      throw new TypeError(`Invalid session ${session}.`);
    }
    if (!isObject(layout)) {
      throw new TypeError(`Invalid layout ${layout}.`);
    }
    if (!isObject(viewerRegistry)) {
      throw new TypeError(`Invalid viewer registry ${viewerRegistry}.`);
    }
    this.cycleIndex = 0;
    this.emitter = new EventEmitter();
    this.layout = layout;
    this.session = session;
    this.viewerRegistry = viewerRegistry;
  }

  /** @inheritdoc */
  destroy() {
    // Není potřeba nic rušit.
    // Přítomno pouze pro splnění rozhraní.
  }

  // TODO #532: Pozor na to, že ScheduleBackground nese médium a tudíž
  // by volání ScheduleInfo.getPlayDuration() nemělo hodit chybu, jenže nyní
  // se tato položka nevyplňuje kvůli nezaobalení Schedule{Region,Orphan},
  // takže nyní by to spadlo! Teda pokud pozadí šablony je obrázek, pokud
  // je to barva pozadí (nyní neimplementovaná), tak getPlayDuration() by
  // se pochopitelně volat nemělo.
  /**
   * Určí informace pro přehrání média.
   * @param {number} cycleIndex Pořadí cyklu, ke kterému vytvořit informace.
   * @private
   * @returns {ScheduleInfo} Informace pro přehrání média.
   */
  getInfo(cycleIndex) {
    const iterationIndex = 0;
    // TODO #532: Proč se další iterace tvoří neúplně naplněné?
    if (cycleIndex > 0) {
      return new ScheduleInfo({ cycleIndex, iterationIndex });
    }
    const { layout } = this;
    const { medium } = layout;
    if (isNil(medium)) {
      // TODO #270 Jak zobrazovat samostatné pozadí?
      return new ScheduleInfo({ cycleIndex, iterationIndex });
    }
    // Napojení na registr informací o typu médiu.
    return new ScheduleInfo({
      cycleIndex,
      iterationIndex,
      layout,
      medium,
      session: this.session,
      viewerRegistry: this.viewerRegistry
    });
  }

  /** @inheritdoc */
  go() {
    const cycleIndex = this.cycleIndex;
    const scheduleInfo = this.getInfo(cycleIndex);
    this.cycleIndex = cycleIndex + 1;
    // Vyvolání události a vrácení výsledku.
    this.emitter.emit('gone', scheduleInfo);
    return scheduleInfo;
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /** @inheritdoc */
  plan(offset) {
    if (!Numeric.isIndex(offset)) {
      throw new TypeError(`Invalid offset ${offset}.`);
    }
    return Array(offset)
      .fill()
      .map((value, index) => this.getInfo(this.cycleIndex + index));
  }

  /** @inheritdoc */
  skip() {
    // Není potřeba nic přeskakovat.
    // Přítomno pouze pro splnění rozhraní.
  }

  /** @inheritdoc */
  stop() {
    // Není potřeba nic zastavovat.
    // Přítomno pouze pro splnění rozhraní.
  }

  /** @inheritdoc */
  toString() {
    return `ScheduleBackground {layoutId: ${this.layout.id}}`;
  }
}

export default ScheduleBackground;
